import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import supportIssuesApi from '../../utils/supportIssuesApi';
import { tiptapJsonToAdf, hasRealContent } from '../../utils/adf';
import { PRIORITY_OPTIONS } from '../../types/support';
import { mapPriorityPtToApiId } from '../../utils/supportMappers';
import RichTextEditor from './RichTextEditor';
import SupportCreateAttachmentDropzone from './SupportCreateAttachmentDropzone';

const PRIMARY = '#f25d27';
const PRIMARY_DARK = '#d44e1f';

const fieldSx = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
  '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
};

const PRIORITY_ITEMS = PRIORITY_OPTIONS.filter((p) => p !== 'Todas' && p !== 'Não definida');

/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   onCreated: () => void;
 *   onReloadFallback: () => void;
 * }} props
 */
export default function SupportCreateIssueDialog({ open, onClose, onCreated, onReloadFallback }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const editorRef = useRef(null);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState(/** @type {import('../../types/support').PriorityPt} */ ('Médio'));
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));

  const isTitleValid = title.trim().length > 0;

  function reset() {
    setTitle('');
    setPriority('Médio');
    setFiles([]);
    setCreating(false);
    setError(null);
    editorRef.current?.clear();
  }

  function handleClose() {
    if (creating) return;
    reset();
    onClose();
  }

  async function handleCreate() {
    setError(null);

    if (!isTitleValid) {
      setError('O título é obrigatório.');
      return;
    }

    const json = editorRef.current?.getJson();
    if (!json || !hasRealContent(json)) {
      setError('A descrição é obrigatória.');
      return;
    }

    const descAdf = tiptapJsonToAdf(json);

    setCreating(true);
    try {
      const createResp = await supportIssuesApi.createIssue({
        request: {
          fields: {
            summary: title.trim(),
            description: descAdf,
            priority: { id: mapPriorityPtToApiId(priority) },
          },
        },
      });

      if (!createResp.external_id) {
        setError('Resposta inválida ao criar chamado (sem external_id).');
        setCreating(false);
        return;
      }

      const externalId = String(createResp.external_id);

      // Upload de anexos sequencial
      for (const file of files) {
        try {
          await supportIssuesApi.uploadAttachment(externalId, file);
        } catch {
          // Não bloquear criação por falha de anexo
        }
      }

      // A lista sempre é repopulada a partir de uma busca completa,
      // garantindo que o chamado criado reflita exatamente o estado do servidor.
      // O modal fecha e a tela já entra em estado de carregamento imediatamente;
      // o atraso antes da busca real fica a cargo de onReloadFallback (SupportPage).
      reset();
      onClose();
      onCreated();
      onReloadFallback();
    } catch (err) {
      setError('Erro ao criar chamado.');
      setCreating(false);
    }
  }

  const canSubmit = isTitleValid && !creating;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px' } }}
    >
      <DialogTitle sx={{ fontWeight: 600, color: '#333', pb: 1 }}>
        Criar Chamado
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          label="Título do chamado"
          placeholder="Ex.: Erro ao acessar o painel"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          autoFocus
          disabled={creating}
          sx={fieldSx}
        />

        <FormControl fullWidth sx={fieldSx} disabled={creating}>
          <InputLabel>Prioridade</InputLabel>
          <Select
            label="Prioridade"
            value={priority}
            onChange={(e) => setPriority(/** @type {any} */ (e.target.value))}
          >
            {PRIORITY_ITEMS.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box>
          <Typography sx={{ fontSize: '0.82rem', color: '#666', mb: 0.75 }}>
            Descrição <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <RichTextEditor
            ref={editorRef}
            placeholder="Descreva o problema/demanda com o máximo de detalhes possível..."
            minHeight={140}
            maxHeight={300}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: '0.82rem', color: '#666', mb: 0.75 }}>
            Anexos
          </Typography>
          <SupportCreateAttachmentDropzone
            files={files}
            onFilesChange={setFiles}
          />
        </Box>

        {error && (
          <Typography sx={{ fontSize: '0.82rem', color: '#d32f2f' }}>{error}</Typography>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={creating}
          sx={{ textTransform: 'none', color: '#666' }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleCreate}
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            backgroundColor: PRIMARY,
            textTransform: 'none',
            borderRadius: '8px',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': { backgroundColor: PRIMARY_DARK, boxShadow: 'none' },
          }}
        >
          {creating ? 'Criando...' : 'Criar chamado'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
